/**
 * Patients Store
 *
 * Manages patient data and CRUD operations.
 * When VITE_ENABLE_MOCK !== 'true', calls the real tms-api-patient service.
 * Otherwise falls back to local dummy data for demo purposes.
 */
import { defineStore } from 'pinia';
import { isAxiosError } from 'axios';
import i18next from 'i18next';
import { getApiClient } from '@/core/services/api.service';
import { logAuditEvent } from '@/core/services/audit.service';
import { useAuthStore } from '@/stores/auth.store';
import { useReferenceStore } from '@/stores/reference.store';
import { config } from '@/core/config';
import { getApiErrorMessage, isNotFoundError, isDuplicateMrnError } from '@/core/utils/api-error.util';
import { computeDiff } from '@tms-phoenix/ui-vue';
import type { Patient } from '@/modules/patients/types';
import type { PatientStatus, ConsentStatus, AdtStatus } from '@/modules/patients/types';
import {
  fetchPatients as apiFetchPatients,
  getPatientById as apiGetPatientById,
  createPatient as apiCreatePatient,
  updatePatient as apiUpdatePatient,
  deletePatient as apiDeletePatient,
  assignPatientsToStaff,
  unassignPatientsFromStaff,
  dischargePatient as apiDischargePatient,
  readmitPatient as apiReadmitPatient,
} from '@/modules/patients/services/patients.api';
import {
  apiListItemToPatient,
  apiDetailToPatient,
} from '@/modules/patients/services/patient.transforms';
import type { CreatePatientRequest, UpdatePatientRequest, ApiPatientListItem, ApiPatientDetail, ApiPagination, DischargePatientRequest, ReadmitPatientRequest, ApiDischargeResult, ApiReadmitResult } from '@/modules/patients/types';

/** Safely extract items from a list API response (plain array or `{ data, meta }` envelope). */
function extractListItems<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[];
  const env = response as { data?: T[] };
  return Array.isArray(env?.data) ? env.data : [];
}

/**
 * Narrow intersection type for axios responses that the API interceptor has
 * enriched with a top-level `.pagination` property.
 */
type WithPagination<T> = T & { pagination?: ApiPagination };

/** Safely extract pagination metadata from a list API response envelope. */
function extractPagination(response: unknown): {
  totalItems?: number;
  currentPage?: number;
  itemsPerPage?: number;
  hasNextPage?: boolean;
} | undefined {
  if (Array.isArray(response)) return undefined;
  const env = response as {
    meta?: {
      pagination?: {
        totalItems?: number;
        total?: number;
        currentPage?: number;
        itemsPerPage?: number;
        hasNextPage?: boolean;
      };
      total?: number;
    };
    total?: number;
  };
  const p = env?.meta?.pagination;
  // Resolve total from whichever field the endpoint returns:
  //   meta.pagination.totalItems → meta.pagination.total → meta.total → top-level total
  const totalItems =
    p?.totalItems ??
    p?.total ??
    env?.meta?.total ??
    env?.total;
  if (p === undefined && totalItems === undefined) return undefined;
  return {
    totalItems,
    currentPage: p?.currentPage,
    itemsPerPage: p?.itemsPerPage,
    hasNextPage: p?.hasNextPage,
  };
}

/** Safely extract a single item from a detail API response (plain object or `{ data: T }` envelope). */
function extractDetail<T>(response: unknown): T {
  const env = response as { data?: T };
  return (env?.data ?? response) as T;
}

/**
 * Convert a Patient view-model to an API-format snapshot (same shape as UpdatePatientRequest).
 * Used to produce a comparable "old state" when computing audit diffs.
 */
function patientToApiSnapshot(patient: Patient): Record<string, unknown> {
  return {
    hospital_id: patient.hospitalId ?? null,
    mrn: patient.medicalRecordNumber ?? null,
    first_name: patient.firstName ?? patient.name?.split(' ')[0] ?? null,
    middle_name: patient.middleName ?? null,
    last_name: patient.lastName ?? patient.name?.split(' ').slice(1).join(' ') ?? null,
    date_of_birth: patient.dateOfBirthIso ?? patient.birthDateTime ?? null,
    gender: patient.gender === 'male' ? 'M' : patient.gender === 'female' ? 'F' : patient.gender === 'undetermined' ? 'U' : null,
    birth_weight_grams: patient.birthWeightGrams ?? null,
    multiple_birth_ind: patient.multipleBirthInd ?? null,
    multiple_birth_ord: patient.multipleBirthOrd ?? null,
    donor_consent: patient.consent === 'yes' ? true : patient.consent === 'no' ? false : null,
    allergies: (patient.allergies ?? []).map((a) => ({
      allergen_id: a.allergy,
      severity_id: a.severity,
    })),
  };
}

/**
 * Compute a diff between the existing patient state (converted to API format)
 * and the update payload. Returns oldValue/newValue objects containing a
 * `mutatedFields` array plus the actual old and new values of those fields.
 */
function computeAuditDiff(
  existingPatient: Patient,
  updatedBody: Record<string, unknown>,
): { oldValue: Record<string, unknown>; newValue: Record<string, unknown> } {
  const existing = patientToApiSnapshot(existingPatient);
  return computeDiff(existing, updatedBody);
}

/**
 * Build a typed `UpdatePatientRequest` from the raw payload passed to `updatePatient`.
 * When the caller already built a full request (detected by the presence of `hospital_id`),
 * all known API fields are forwarded explicitly so the compiler validates the shape.
 * Otherwise, the view-model `Patient` fields are mapped.
 */
function buildUpdatePatientBody(patientData: Partial<Patient>): UpdatePatientRequest {
  const raw = patientData as Record<string, unknown>;

  if (raw.hospital_id) {
    // Caller already constructed the request body — forward it field-by-field
    // so the compiler validates the shape (no unsafe double-cast).
    // Detecting via hospital_id alone covers callers that legitimately omit mrn
    // (e.g. updating only encounter, allergies, link_id, person_id, etc.).
    return {
      hospital_id: raw.hospital_id as string,
      mrn: raw.mrn as string | undefined,
      first_name: raw.first_name as string | undefined,
      middle_name: raw.middle_name as string | undefined,
      last_name: raw.last_name as string | undefined,
      date_of_birth: raw.date_of_birth as string | undefined,
      gender: raw.gender as import('@/modules/patients/types').GenderCode | null | undefined,
      birth_weight_grams: raw.birth_weight_grams as number | null | undefined,
      multiple_birth_ind: raw.multiple_birth_ind as boolean | undefined,
      multiple_birth_ord: raw.multiple_birth_ord as number | null | undefined,
      encounter: raw.encounter as UpdatePatientRequest['encounter'],
      encounter_location: raw.encounter_location as UpdatePatientRequest['encounter_location'],
      allergies: raw.allergies as UpdatePatientRequest['allergies'],
      link_id: raw.link_id as string | undefined,
      person_id: raw.person_id as string | null | undefined,
      donor_consent: raw.donor_consent as boolean | undefined,
    };
  }

  // Fallback: map view-model Patient fields
  const b: UpdatePatientRequest = { hospital_id: (raw.hospital_id as string | undefined) ?? '' };
  if (patientData.medicalRecordNumber) b.mrn = patientData.medicalRecordNumber;
  if (patientData.name) {
    const parts = patientData.name.split(' ');
    b.first_name = parts[0];
    b.last_name = parts.slice(1).join(' ');
  }
  if (patientData.dateOfBirthIso) b.date_of_birth = patientData.dateOfBirthIso;
  else if (patientData.birthDateTime) b.date_of_birth = patientData.birthDateTime;
  if (patientData.gender) {
    b.gender = patientData.gender === 'male' ? 'M' : patientData.gender === 'female' ? 'F' : 'U';
  }
  if (patientData.consent !== undefined) {
    b.donor_consent = patientData.consent === 'yes';
  }
  return b;
}

export type PatientViewFilter = 'my' | 'all';

/**
 * Maps UI PatientStatus values to one or more API acuity_level strings.
 * 'SERIOUS' covers both MODERATE and SERIOUS because the API stores either
 * value for patients the UI labels as Serious.
 */
const UI_TO_ACUITY_MAP: Record<string, ('STABLE' | 'MODERATE' | 'SERIOUS' | 'CRITICAL' | 'LIFE_THREATENING')[]> = {
  STABLE: ['STABLE'],
  SERIOUS: ['MODERATE', 'SERIOUS'],
  CRITICAL: ['CRITICAL'],
  LIFE_THREATENING: ['LIFE_THREATENING'],
};

/**
 * Maps the ADT status checkbox selection to the API's `is_active` query param.
 * Both selected, or neither selected (e.g. after "Clear" filters) -> 'all' (no
 * filter, returns both active and discharged). Only 'discharged' selected ->
 * 'false'. Only 'active' selected -> 'true'.
 */
function adtStatusFiltersToIsActiveParam(filters: AdtStatus[]): 'true' | 'false' | 'all' {
  const hasActive = filters.includes('active');
  const hasDischarged = filters.includes('discharged');
  if (hasActive && hasDischarged) return 'all';
  if (!hasActive && !hasDischarged) return 'all';
  if (hasDischarged) return 'false';
  return 'true';
}

/**
 * Whether a patient still belongs in the currently-displayed list given the
 * selected ADT status checkboxes. Both (or neither) selected -> no filtering.
 * Used after discharge/readmit to prune a row that just crossed out of the
 * active filter (e.g. discharging a patient while the "Active"-only filter
 * is applied) so it disappears immediately instead of lingering until the
 * next full refetch.
 */
function matchesAdtStatusFilter(patient: Patient, filters: AdtStatus[]): boolean {
  const hasActive = filters.includes('active');
  const hasDischarged = filters.includes('discharged');
  if (hasActive && !hasDischarged) return patient.isActive !== false;
  if (hasDischarged && !hasActive) return patient.isActive === false;
  return true;
}

export interface PatientsQueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  statusFilters?: PatientStatus[];
  consentFilters?: ConsentStatus[];
  viewFilter?: PatientViewFilter;
  /** Date-of-birth filter from value (UTC ISO string). */
  dobFrom?: string;
  /** Date-of-birth filter to value (UTC ISO string). */
  dobTo?: string;
  /** ADT status filter: which of 'active'/'discharged' to include. Defaults to ['active'] (active only). */
  adtStatusFilters?: AdtStatus[];
  /**
   * One-off override for the API's `is_active` param that does NOT persist into
   * `activeAdtStatusFilters` (unlike `adtStatusFilters`). Use this from callers
   * that need a guaranteed active-only (or all) patient set regardless of
   * whatever ADT filter the Patient List page currently has selected (e.g. a
   * "New Feed Order" patient picker must never leak discharged patients just
   * because the list page happens to be filtered to "Discharged").
   */
  isActiveOverride?: 'true' | 'false' | 'all';
}

interface PatientsState {
  patients: Patient[];
  total: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  selectedPatientIds: Set<string>;
  activeView: PatientViewFilter;
  activeSearch: string;
  activeStatusFilters: PatientStatus[];
  activeConsentFilters: ConsentStatus[];
  activeDobFrom: string;
  activeDobTo: string;
  activeAdtStatusFilters: AdtStatus[];
  myPatientsTotal: number;
  allPatientsTotal: number;
  /** IDs of patients assigned to the current user (for isAssignedToMe). */
  myPatientIds: Set<string>;
  /** Authoritative workload count from the API (patient-staff/staff/:staffId/patient-count). null = not yet loaded. */
  myWorkloadCount: number | null;
  _allPatientsCache: Patient[] | null;
  _cacheUserEmail: string;
  /**
   * Monotonically-incrementing counter bumped at the start of every
   * fetchPatients() call. Lets in-flight requests detect that a newer fetch
   * (with different filter values) has since been issued, so a slow/stale
   * response never overwrites the list with results for outdated filters.
   */
  _fetchRequestSeq: number;
  /** Result of the most recent dischargePatient() call's mother-auto-discharge outcome, if the patient had a linked mother. Null when no linked mother or before any discharge call. */
  lastMotherAutoDischarge: { triggered: boolean; personsDischarged: string[] } | null;
  /** Result of the most recent readmitPatient() call's mother-auto-readmit outcome, if the patient had a linked mother. Null when no linked mother or before any readmit call. */
  lastMotherAutoReadmit: { triggered: boolean; personId: string } | null;
  /** Count of PREPARED-but-not-administered feed splits auto-updated to SEND_HOME by the most recent dischargePatient() call. Null when not yet known. */
  lastPreparedFeedsSentHomeCount: number | null;
}

export const usePatientsStore = defineStore('patients', {
  state: (): PatientsState => ({
    patients: [],
    total: 0,
    currentPage: 1,
    pageSize: 10,
    isLoading: false,
    isLoadingMore: false,
    hasMore: true,
    error: null,
    selectedPatientIds: new Set(),
    activeView: 'all' as PatientViewFilter,
    activeSearch: '',
    activeStatusFilters: [],
    activeConsentFilters: [],
    activeDobFrom: '',
    activeDobTo: '',
    activeAdtStatusFilters: ['active'] as AdtStatus[],
    myPatientsTotal: 0,
    allPatientsTotal: 0,
    myPatientIds: new Set(),
    myWorkloadCount: null,
    _allPatientsCache: null,
    _cacheUserEmail: '',
    _fetchRequestSeq: 0,
    lastMotherAutoDischarge: null,
    lastMotherAutoReadmit: null,
    lastPreparedFeedsSentHomeCount: null,
  }),

  getters: {
    hasPatients: (state): boolean => state.patients.length > 0,
    totalPages: (state): number => Math.ceil(state.total / state.pageSize),
    selectedCount: (state): number => state.selectedPatientIds.size,
    selectedPatients: (state): Patient[] =>
      state.patients.filter(p => state.selectedPatientIds.has(p.id)),
    /** The effective user email used for patient assignment matching. */
    currentUserEmail: (): string => {
      const authStore = useAuthStore();
      return authStore.user?.email || '';
    },
    /** Whether we should use mock/dummy data instead of the real API. */
    useMockData: (): boolean => config.features.enableMockData,
  },

  actions: {
    // Generate dummy data for demo -- returns cached list for consistent pagination
    generateDummyPatients(): Patient[] {
      const userEmail = this.currentUserEmail;

      // Invalidate cache when the authenticated user changes
      if (this._allPatientsCache && this._cacheUserEmail === userEmail) {
        return this._allPatientsCache;
      }
      this._allPatientsCache = null;

      // Name building blocks for generated patients
      const lastNames = [
        'Anderson', 'Baker', 'Bennett', 'Brooks', 'Butler', 'Campbell', 'Carter',
        'Chavez', 'Coleman', 'Collins', 'Cooper', 'Cox', 'Cruz', 'Diaz', 'Edwards',
        'Ellis', 'Evans', 'Fisher', 'Fleming', 'Foster', 'Garcia', 'Gibson', 'Gomez',
        'Gonzalez', 'Graham', 'Green', 'Griffin', 'Gutierrez', 'Hamilton', 'Hansen',
        'Harrison', 'Hayes', 'Henderson', 'Hernandez', 'Hill', 'Howard', 'Hughes',
        'Hunt', 'James', 'Jenkins', 'Jordan', 'Kelley', 'Kennedy', 'Kim', 'King',
        'Knight', 'Lane', 'Lawrence', 'Long', 'Lopez', 'Marshall', 'Mason', 'Mendez',
        'Mitchell', 'Morgan', 'Morris', 'Murphy', 'Murray', 'Nelson', 'Nguyen',
        'Oliver', 'Ortiz', 'Owen', 'Palmer', 'Parker', 'Patterson', 'Perry', 'Peters',
        'Phillips', 'Powell', 'Price', 'Quinn', 'Ramirez', 'Reed', 'Reyes', 'Reynolds',
        'Richardson', 'Riley', 'Rivera', 'Roberts', 'Robinson', 'Rogers', 'Ross',
        'Ruiz', 'Russell', 'Sanders', 'Sanchez', 'Scott', 'Shaw', 'Simmons', 'Singh',
        'Spencer', 'Stevens', 'Stewart', 'Stone', 'Sullivan', 'Turner', 'Vargas',
        'Wagner', 'Wallace', 'Ward', 'Watson', 'Webb', 'West', 'Williams', 'Wilson',
        'Wood', 'Wright', 'Young',
      ];
      const locations = ['NICU-01', 'NICU-02', 'NICU-3 B801', 'NICU-4 A303', 'NICU-05', 'NICU-06', 'NICU-07'];
      const statuses: PatientStatus[] = ['STABLE', 'SERIOUS', 'CRITICAL', 'LIFE_THREATENING'];
      const consents: ConsentStatus[] = ['yes', 'no'];
      const genders: ('male' | 'female')[] = ['male', 'female'];

      // Deterministic helper -- uses index to pick values consistently
      const pick = <T>(arr: T[], idx: number): T => arr[idx % arr.length];
      const pad = (n: number): string => String(n).padStart(2, '0');

      // Original 25 hand-crafted patients
      const dummyPatients: Patient[] = [
        { id: '1', name: 'Baby Girl Rodriguez', birthDateTime: '15/03/2026 08:30', gender: 'female', medicalRecordNumber: 'MRN-6002', location: 'NICU-3 B801', status: 'STABLE', consent: 'yes', email: 'emma.j@email.com', phone: '555-0101', createdAt: '2026-02-01T10:00:00Z', updatedAt: '2026-02-18T09:00:00Z' },
        { id: '2', name: 'Baby Boy Thompson', birthDateTime: '22/07/2026 14:45', gender: 'male', medicalRecordNumber: 'MRN-4000', location: 'NICU-4 A303', status: 'STABLE', consent: 'yes', email: 'james.smith@email.com', phone: '555-0102', createdAt: '2026-02-02T11:30:00Z', updatedAt: '2026-02-18T08:45:00Z' },
        { id: '3', name: 'Little John', birthDateTime: '08/11/2026 03:20', gender: 'male', medicalRecordNumber: 'MRN-1001', location: 'NICU-01', status: 'SERIOUS', consent: 'no', email: 'sarah.w@email.com', phone: '555-0103', createdAt: '2026-02-03T14:00:00Z', updatedAt: '2026-02-18T10:15:00Z' },
        { id: '4', name: 'Small Wonder', birthDateTime: '30/04/2026 16:10', gender: 'female', medicalRecordNumber: 'Demo-Pat-330', location: 'NICU-02', status: 'STABLE', consent: 'yes', email: 'michael.b@email.com', phone: '555-0104', createdAt: '2026-02-04T09:20:00Z', updatedAt: '2026-02-18T07:30:00Z' },
        { id: '5', name: 'TestBaby Unlinked', birthDateTime: '25/12/2026 23:55', gender: 'female', medicalRecordNumber: 'Demo-Pat-2210', location: 'NICU-05', status: 'STABLE', consent: 'yes', email: 'olivia.d@email.com', phone: '555-0105', createdAt: '2026-02-05T16:45:00Z', updatedAt: '2026-02-18T11:00:00Z' },
        { id: '6', name: 'Kyle Connor', birthDateTime: '14/06/2026 11:25', gender: 'male', medicalRecordNumber: 'MRN-7543', location: 'NICU-3 B801', status: 'SERIOUS', consent: 'yes', email: 'william.m@email.com', phone: '555-0106', createdAt: '2026-02-06T08:00:00Z', updatedAt: '2026-02-18T09:45:00Z' },
        { id: '7', name: 'John Connor', birthDateTime: '03/09/2026 07:15', gender: 'male', medicalRecordNumber: 'MRN-2156', location: 'NICU-4 A303', status: 'STABLE', consent: 'no', email: 'sophia.g@email.com', phone: '555-0107', createdAt: '2026-02-07T12:30:00Z', updatedAt: '2026-02-18T08:20:00Z' },
        { id: '8', name: 'Newt Ripley', birthDateTime: '19/01/2026 19:40', gender: 'female', medicalRecordNumber: 'Demo-Pat-445', location: 'NICU-01', status: 'CRITICAL', consent: 'yes', email: 'benjamin.r@email.com', phone: '555-0108', createdAt: '2026-02-08T10:15:00Z', updatedAt: '2026-02-18T10:50:00Z' },
        { id: '9', name: 'Baby Jones', birthDateTime: '27/05/2026 13:05', gender: 'female', medicalRecordNumber: 'MRN-8921', location: 'NICU-02', status: 'STABLE', consent: 'yes', email: 'isabella.w@email.com', phone: '555-0109', createdAt: '2026-02-09T15:00:00Z', updatedAt: '2026-02-18T07:15:00Z' },
        { id: '10', name: 'Baby Miller', birthDateTime: '11/08/2026 05:50', gender: 'male', medicalRecordNumber: 'MRN-3344', location: 'NICU-05', status: 'SERIOUS', consent: 'yes', email: 'alex.a@email.com', phone: '555-0110', createdAt: '2026-02-10T09:45:00Z', updatedAt: '2026-02-18T09:30:00Z' },
        { id: '11', name: 'Mia Thomas', birthDateTime: '05/10/2026 22:10', gender: 'female', medicalRecordNumber: 'Demo-Pat-1122', location: 'NICU-3 B801', status: 'STABLE', consent: 'no', email: 'mia.t@email.com', phone: '555-0111', createdAt: '2026-02-11T11:20:00Z', updatedAt: '2026-02-18T08:00:00Z' },
        { id: '12', name: 'Ethan Jackson', birthDateTime: '28/02/2026 10:35', gender: 'male', medicalRecordNumber: 'MRN-5678', location: 'NICU-4 A303', status: 'CRITICAL', consent: 'yes', email: 'ethan.j@email.com', phone: '555-0112', createdAt: '2026-02-12T13:10:00Z', updatedAt: '2026-02-18T11:20:00Z' },
        { id: '13', name: 'Charlotte White', birthDateTime: '16/04/2026 15:20', gender: 'female', medicalRecordNumber: 'MRN-9034', location: 'NICU-01', status: 'SERIOUS', consent: 'yes', email: 'charlotte.w@email.com', phone: '555-0113', createdAt: '2026-02-13T08:30:00Z', updatedAt: '2026-02-18T10:05:00Z' },
        { id: '14', name: 'Daniel Harris', birthDateTime: '09/07/2026 06:45', gender: 'male', medicalRecordNumber: 'Demo-Pat-567', location: 'NICU-02', status: 'STABLE', consent: 'yes', email: 'daniel.h@email.com', phone: '555-0114', createdAt: '2026-02-14T10:00:00Z', updatedAt: '2026-02-18T07:45:00Z' },
        { id: '15', name: 'Amelia Martin', birthDateTime: '21/11/2026 18:30', gender: 'female', medicalRecordNumber: 'MRN-1234', location: 'NICU-05', status: 'STABLE', consent: 'yes', email: 'amelia.m@email.com', phone: '555-0115', createdAt: '2026-02-15T14:25:00Z', updatedAt: '2026-02-18T11:10:00Z' },
        { id: '16', name: 'Matthew Thompson', birthDateTime: '12/03/2026 12:15', gender: 'male', medicalRecordNumber: 'Demo-Pat-789', location: 'NICU-3 B801', status: 'STABLE', consent: 'no', email: 'matthew.t@email.com', phone: '555-0116', createdAt: '2026-02-16T09:15:00Z', updatedAt: '2026-02-18T08:35:00Z' },
        { id: '17', name: 'Harper Moore', birthDateTime: '07/06/2026 21:05', gender: 'female', medicalRecordNumber: 'MRN-4567', location: 'NICU-4 A303', status: 'SERIOUS', consent: 'yes', email: 'harper.m@email.com', phone: '555-0117', createdAt: '2026-02-17T11:50:00Z', updatedAt: '2026-02-18T09:55:00Z' },
        { id: '18', name: 'Joseph Taylor', birthDateTime: '24/09/2026 04:25', gender: 'male', medicalRecordNumber: 'MRN-7890', location: 'NICU-01', status: 'STABLE', consent: 'yes', email: 'joseph.t@email.com', phone: '555-0118', createdAt: '2026-02-18T07:00:00Z', updatedAt: '2026-02-18T10:30:00Z' },
        { id: '19', name: 'Evelyn Lee', birthDateTime: '18/12/2026 09:40', gender: 'female', medicalRecordNumber: 'Demo-Pat-998', location: 'NICU-02', status: 'CRITICAL', consent: 'yes', email: 'evelyn.l@email.com', phone: '555-0119', createdAt: '2026-02-18T08:20:00Z', updatedAt: '2026-02-18T11:25:00Z' },
        { id: '20', name: 'David Perez', birthDateTime: '02/05/2026 17:55', gender: 'male', medicalRecordNumber: 'MRN-2345', location: 'NICU-05', status: 'SERIOUS', consent: 'no', email: 'david.p@email.com', phone: '555-0120', createdAt: '2026-02-18T09:10:00Z', updatedAt: '2026-02-18T10:40:00Z' },
        { id: '21', name: 'Abigail Clark', birthDateTime: '29/08/2026 14:10', gender: 'female', medicalRecordNumber: 'Demo-Pat-1567', location: 'NICU-3 B801', status: 'STABLE', consent: 'yes', email: 'abigail.c@email.com', phone: '555-0121', createdAt: '2026-02-18T10:05:00Z', updatedAt: '2026-02-18T11:15:00Z' },
        { id: '22', name: 'Christopher Lewis', birthDateTime: '14/01/2026 20:30', gender: 'male', medicalRecordNumber: 'MRN-6789', location: 'NICU-4 A303', status: 'CRITICAL', consent: 'yes', email: 'chris.l@email.com', phone: '555-0122', createdAt: '2026-02-18T10:30:00Z', updatedAt: '2026-02-18T11:30:00Z' },
        { id: '23', name: 'Emily Walker', birthDateTime: '26/10/2026 11:50', gender: 'female', medicalRecordNumber: 'MRN-3456', location: 'NICU-01', status: 'STABLE', consent: 'yes', email: 'emily.w@email.com', phone: '555-0123', createdAt: '2026-02-18T11:00:00Z', updatedAt: '2026-02-18T11:35:00Z' },
        { id: '24', name: 'Joshua Hall', birthDateTime: '20/04/2026 08:05', gender: 'male', medicalRecordNumber: 'Demo-Pat-3389', location: 'NICU-02', status: 'SERIOUS', consent: 'no', email: 'joshua.h@email.com', phone: '555-0124', createdAt: '2026-02-18T11:15:00Z', updatedAt: '2026-02-18T11:40:00Z' },
        { id: '25', name: 'Elizabeth Allen', birthDateTime: '31/07/2026 15:25', gender: 'female', medicalRecordNumber: 'MRN-8901', location: 'NICU-05', status: 'STABLE', consent: 'yes', email: 'elizabeth.a@email.com', phone: '555-0125', createdAt: '2026-02-18T11:20:00Z', updatedAt: '2026-02-18T11:45:00Z' },
      ];

      // Generate 75 more patients programmatically (ids 26-100)
      for (let i = 26; i <= 100; i++) {
        const gender = pick(genders, i);
        const prefix = gender === 'male' ? 'Baby Boy' : 'Baby Girl';
        const lastName = pick(lastNames, i);
        const day = pad(((i * 7) % 28) + 1);
        const month = pad(((i * 3) % 12) + 1);
        const hour = pad((i * 5) % 24);
        const minute = pad((i * 11) % 60);
        const mrnNum = 1000 + i * 37;
        const mrnPrefix = i % 3 === 0 ? 'Demo-Pat' : 'MRN';

        dummyPatients.push({
          id: String(i),
          name: `${prefix} ${lastName}`,
          birthDateTime: `${day}/${month}/2026 ${hour}:${minute}`,
          gender,
          medicalRecordNumber: `${mrnPrefix}-${mrnNum}`,
          location: pick(locations, i),
          status: pick(statuses, i),
          consent: pick(consents, i),
          email: `${lastName.toLowerCase()}.${i}@email.com`,
          phone: `555-${pad(Math.floor(i / 100))}${pad(i % 100)}`,
          createdAt: `2026-02-${pad(((i - 1) % 28) + 1)}T${hour}:${minute}:00Z`,
          updatedAt: `2026-02-28T${hour}:${minute}:00Z`,
        });
      }

      // Shuffle all patients for random order
      if (dummyPatients.length > 1) {
        for (let i = dummyPatients.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [dummyPatients[i], dummyPatients[j]] = [dummyPatients[j], dummyPatients[i]];
        }
      }
      // Assign 15 random patients to the current user for demo
      if (userEmail && dummyPatients.length > 0) {
        const indices = Array.from({ length: dummyPatients.length }, (_, i) => i);
        for (let i = indices.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [indices[i], indices[j]] = [indices[j], indices[i]];
        }
        const assignedIndices = indices.slice(0, 15);
        for (let i = 0; i < assignedIndices.length; i++) {
          dummyPatients[assignedIndices[i]].email = userEmail;
        }
      }

      this._allPatientsCache = dummyPatients;
      this._cacheUserEmail = userEmail;
      return dummyPatients;
    },

    /**
     * Returns the user's email for "My Patients" matching.
     * Delegates to the public `currentUserEmail` getter.
     */
    _getUserEmail(): string {
      return this.currentUserEmail;
    },

    /**
     * Returns the subset of cached patients that matches the given view filter.
     * 'my' -> only patients assigned to the current user; 'all' -> every patient.
     */
    _getViewPatients(view: PatientViewFilter): Patient[] {
      const allPatients = this.generateDummyPatients();
      if (view === 'all') return allPatients;
      const userEmail = this._getUserEmail();
      return allPatients.filter(p => p.email === userEmail);
    },

    /**
     * Returns patients filtered by view + status + consent + search.
     * Pagination should be applied on top of this result.
     */
    _getFilteredPatients(params?: PatientsQueryParams): Patient[] {
      const view = params?.viewFilter ?? this.activeView;
      let result = this._getViewPatients(view);

      // Status filter
      const statusFilters = params?.statusFilters ?? this.activeStatusFilters;
      if (statusFilters.length > 0) {
        result = result.filter(p => statusFilters.includes(p.status));
      }

      // Consent filter
      const consentFilters = params?.consentFilters ?? this.activeConsentFilters;
      if (consentFilters.length > 0) {
        result = result.filter(p => consentFilters.includes(p.consent));
      }

      // ADT status filter -- both (or neither) selected means no filtering.
      const adtStatusFilters = params?.adtStatusFilters ?? this.activeAdtStatusFilters;
      const hasActive = adtStatusFilters.includes('active');
      const hasDischarged = adtStatusFilters.includes('discharged');
      if (hasDischarged && !hasActive) {
        result = result.filter(p => p.isActive === false);
      } else if (hasActive && !hasDischarged) {
        result = result.filter(p => p.isActive !== false);
      }

      // Search filter (name or MRN) -- minimum 2 characters required
      const search = (params?.search ?? this.activeSearch).toLowerCase();
      if (search.length >= 2) {
        result = result.filter(p =>
          p.name.toLowerCase().includes(search) ||
          p.medicalRecordNumber.toLowerCase().includes(search)
        );
      }

      return result;
    },

    /**
     * Pre-load IDs of patients assigned to the current user.
     * In mock mode builds the set from the email field; in real mode
     * calls the API with staff_id filter.
     */
    async loadMyPatientIds() {
      if (this.useMockData) {
        const email = this._getUserEmail();
        const all = this.generateDummyPatients();
        const assigned = all.filter((p) => p.email === email);
        this.myPatientIds = new Set(assigned.map((p) => p.id));
        // Set the My Patients count immediately so the tab shows the correct
        // value on initial page load without waiting for fetchPatients.
        this.myPatientsTotal = assigned.length;
        return;
      }
      const staffId = useAuthStore().user?.id || config.staffId;
      if (!staffId) return;
      try {
        const myIdsRes = await apiFetchPatients({
          staff_id: staffId,
          limit: 100,
        });
        const listItems: ApiPatientListItem[] = extractListItems<ApiPatientListItem>(myIdsRes.data);
        const pagination =
          (myIdsRes as WithPagination<typeof myIdsRes>).pagination ??
          extractPagination(myIdsRes.data);
        this.myPatientIds = new Set(listItems.map((p) => p.patient_id));
        // Use pagination total so the count reflects all assigned patients,
        // not just the first page (limit: 100).
        this.myPatientsTotal = pagination?.totalItems ?? listItems.length;
      } catch {
        // Non-critical -- assignment icons may be missing
      }
    },

    /**
     * Enrich the current `this.patients` array with:
     * - `isAssignedToMe` based on `myPatientIds`
     * - Resolved `location` from the reference store name maps.
     *   Always overwrites location for patients that carry careUnitId/bedId so
     *   stale or raw UUID strings are never shown when reference data isn't loaded.
     */
    _enrichPatients() {
      const referenceStore = useReferenceStore();
      for (const p of this.patients) {
        p.isAssignedToMe = this.myPatientIds.has(p.id);
        if (p.careUnitId || p.bedId) {
          // Unconditionally assign -- resolveLocation returns '' when reference
          // data is not yet loaded, which is preferable to showing raw UUIDs.
          p.location = referenceStore.resolveLocation(
            p.careUnitId || '',
            p.bedId || '',
          );
        }
      }
    },

    async fetchPatients(params?: PatientsQueryParams): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      // Bump the request sequence so any earlier in-flight fetchPatients()/
      // loadMorePatients() call can detect it's now stale (filter values have
      // since changed) and skip applying its response to this.patients.
      this._fetchRequestSeq += 1;
      const requestSeq = this._fetchRequestSeq;

      try {
        // Persist active filter state
        if (params?.viewFilter !== undefined) this.activeView = params.viewFilter;
        if (params?.search !== undefined) this.activeSearch = params.search;
        if (params?.statusFilters !== undefined) this.activeStatusFilters = params.statusFilters;
        if (params?.consentFilters !== undefined) this.activeConsentFilters = params.consentFilters;
        if (params?.dobFrom !== undefined) this.activeDobFrom = params.dobFrom;
        if (params?.dobTo !== undefined) this.activeDobTo = params.dobTo;
        if (params?.adtStatusFilters !== undefined) this.activeAdtStatusFilters = params.adtStatusFilters;

        if (!this.useMockData) {
          // -- Real API call --
          const staffId =
            params?.viewFilter === 'my' ? (useAuthStore().user?.id || config.staffId) : undefined;

          const statusFilters = params?.statusFilters ?? this.activeStatusFilters;
          // Expand each UI status to its corresponding API acuity_level value(s)
          // and send the full multi-value array so the server filters correctly.
          const apiStatuses = statusFilters.length > 0
            ? statusFilters.flatMap(s => UI_TO_ACUITY_MAP[s] ?? [])
            : undefined;

          const consentFilters = params?.consentFilters ?? this.activeConsentFilters;
          // Map UI consent values to a single boolean API param.
          // Only send when exactly one value is selected; omit when both or neither.
          const donorConsentParam: boolean | undefined =
            consentFilters.length === 1
              ? consentFilters[0] === 'yes'
              : undefined;

          const fetchRes = await apiFetchPatients({
            page: params?.page || 1,
            limit: params?.pageSize || 20,
            search: (params?.search ?? this.activeSearch) || undefined,
            status: apiStatuses,
            staff_id: staffId,
            ...(donorConsentParam !== undefined && { donor_consent: donorConsentParam }),
            ...(this.activeDobFrom && { from: this.activeDobFrom }),
            ...(this.activeDobTo && { to: this.activeDobTo }),
            is_active: params?.isActiveOverride ?? adtStatusFiltersToIsActiveParam(this.activeAdtStatusFilters),
          });

          // The createApiClient interceptor unwraps response.data to the raw array
          // and moves pagination to axiosResponse.pagination.
          const listItems: ApiPatientListItem[] = extractListItems<ApiPatientListItem>(fetchRes.data);
          const pagination =
            (fetchRes as WithPagination<typeof fetchRes>).pagination ??
            extractPagination(fetchRes.data);

          // A newer fetchPatients() call (e.g. the user changed a filter while
          // this request was in flight) has since started -- discard this
          // stale response so the list always reflects the latest filter
          // values rather than being overwritten by an out-of-order reply.
          if (requestSeq !== this._fetchRequestSeq) {
            return false;
          }

          let mappedPatients = listItems.map(apiListItemToPatient);

          // Client-side status filter: handles multiple selected statuses and
          // ensures the UI-mapped status values (admitted / in-progress / critical)
          // correctly narrow the results even when the API ignores the query param.
          if (statusFilters.length > 0) {
            mappedPatients = mappedPatients.filter(p => statusFilters.includes(p.status));
          }

          this.patients = mappedPatients;
          this.total = pagination?.totalItems ?? listItems.length;
          this.currentPage = pagination?.currentPage ?? 1;
          this.pageSize = pagination?.itemsPerPage ?? 20;
          this.hasMore = pagination?.hasNextPage ?? false;
          // Only update the count for the view that was just fetched.
          // myPatientsTotal is managed by loadMyWorkloadCount (authoritative staff count).
          if (params?.viewFilter === 'my') {
            this.myPatientsTotal = this.total;
          } else {
            this.allPatientsTotal = this.total;
          }
          this._enrichPatients();
          this.deselectAllPatients();
          return true;
        }

        // -- Mock / dummy data fallback --
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay

        // Discard this response if a newer fetchPatients() call has since
        // started (same race-guard as the real-API branch above).
        if (requestSeq !== this._fetchRequestSeq) {
          return false;
        }

        const filtered = this._getFilteredPatients(params);

        const page = params?.page || 1;
        const pageSize = params?.pageSize || 10;
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        this.patients = filtered.slice(startIndex, endIndex);
        this.total = filtered.length;
        this.currentPage = page;
        this.pageSize = pageSize;
        this.hasMore = endIndex < filtered.length;

        // Enrich mock patients with assignment flags
        this._enrichPatients();

        // Clear selection when the displayed dataset changes
        this.deselectAllPatients();

        // Keep both tab counts up-to-date (unfiltered by status/consent/search)
        this.allPatientsTotal = this._getViewPatients('all').length;
        this.myPatientsTotal = this._getViewPatients('my').length;

        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'apiErrors.failedFetchPatients');
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    async loadMorePatients(params?: PatientsQueryParams): Promise<boolean> {
      if (this.isLoadingMore || !this.hasMore) return false;

      this.isLoadingMore = true;
      this.error = null;

      // Snapshot the current sequence -- if a fetchPatients() call (e.g. the
      // user changed a filter) starts while this request is in flight, that
      // bumps _fetchRequestSeq and this response is discarded below instead
      // of appending rows that no longer match the active filters.
      const requestSeq = this._fetchRequestSeq;

      try {
        if (!this.useMockData) {
          // -- Real API call --
          const nextPage = this.currentPage + 1;
          const staffId =
            this.activeView === 'my' ? (useAuthStore().user?.id || config.staffId) : undefined;

          const apiStatuses = this.activeStatusFilters.length > 0
            ? this.activeStatusFilters.flatMap(s => UI_TO_ACUITY_MAP[s] ?? [])
            : undefined;

          const loadMoreDonorConsentParam: boolean | undefined =
            this.activeConsentFilters.length === 1
              ? this.activeConsentFilters[0] === 'yes'
              : undefined;

          const loadMoreRes = await apiFetchPatients({
            page: nextPage,
            limit: this.pageSize,
            search: this.activeSearch || undefined,
            status: apiStatuses,
            staff_id: staffId,
            ...(loadMoreDonorConsentParam !== undefined && { donor_consent: loadMoreDonorConsentParam }),
            ...(this.activeDobFrom && { from: this.activeDobFrom }),
            ...(this.activeDobTo && { to: this.activeDobTo }),
            is_active: adtStatusFiltersToIsActiveParam(this.activeAdtStatusFilters),
          });

          const listItems: ApiPatientListItem[] = extractListItems<ApiPatientListItem>(loadMoreRes.data);
          const pagination =
            (loadMoreRes as WithPagination<typeof loadMoreRes>).pagination ??
            extractPagination(loadMoreRes.data);

          // Discard this page if the active filters have since changed.
          if (requestSeq !== this._fetchRequestSeq) {
            return false;
          }

          let newPatients = listItems.map(apiListItemToPatient);

          // Client-side status filter (handles multiple selected statuses).
          if (this.activeStatusFilters.length > 0) {
            newPatients = newPatients.filter(p => this.activeStatusFilters.includes(p.status));
          }
          this.patients.push(...newPatients);
          this._enrichPatients();
          this.currentPage = pagination?.currentPage ?? nextPage;
          this.hasMore = pagination?.hasNextPage ?? false;
          return true;
        }

        // -- Mock fallback --
        await new Promise(resolve => setTimeout(resolve, 500));

        // Discard this page if the active filters have since changed.
        if (requestSeq !== this._fetchRequestSeq) {
          return false;
        }

        const filtered = this._getFilteredPatients(params);

        const nextPage = this.currentPage + 1;
        const startIndex = (nextPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;

        const newPatients = filtered.slice(startIndex, endIndex);
        this.patients.push(...newPatients);
        this._enrichPatients();
        this.currentPage = nextPage;
        this.hasMore = endIndex < filtered.length;

        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'apiErrors.failedLoadMorePatients');
        return false;
      } finally {
        this.isLoadingMore = false;
      }
    },

    /**
     * Fetch full patient detail from the API.
     *
     * @param silent - When true, skips toggling `isLoading`. Use this for
     * background/preview fetches (e.g. print-label preview) that must not
     * put the whole patient list/table into its loading state.
     */
    async getPatient(id: string, silent = false): Promise<Patient | null> {
      if (!silent) this.isLoading = true;
      this.error = null;

      try {
        if (!this.useMockData) {
          const { data: response } = await apiGetPatientById(id);
          // Normalize: plain object or wrapped envelope
          const detail = extractDetail<ApiPatientDetail>(response);
          const patient = apiDetailToPatient(detail, useReferenceStore().facilities);
          // Enrich isAssignedToMe from the local myPatientIds set
          patient.isAssignedToMe = this.myPatientIds.has(patient.id);
          // Ensure patient is in this.patients so subsequent updatePatient
          // has access to full detail fields for audit diff computation.
          const idx = this.patients.findIndex((p) => p.id === id);
          if (idx !== -1) {
            this.patients[idx] = patient;
          } else {
            this.patients.push(patient);
          }
          return patient;
        }

        // Mock fallback -- look up from local cache
        const all = this.generateDummyPatients();
        return all.find((p) => p.id === id) ?? null;
      } catch (err) {
        this.error = isNotFoundError(err)
          ? getApiErrorMessage(err, 'apiErrors.patientNotFound')
          : getApiErrorMessage(err, 'apiErrors.failedFetchPatient');
        return null;
      } finally {
        if (!silent) this.isLoading = false;
      }
    },

    async createPatient(body: CreatePatientRequest): Promise<Patient | null> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditNewValue: unknown | undefined;

      try {
        if (!this.useMockData) {
          const apiResponse = await apiCreatePatient(body);
          const response = apiResponse.data;
          // Normalize: plain object or wrapped envelope
          const created = extractDetail<ApiPatientDetail>(response);
          // Pass facilities so apiDetailToPatient can fall back to flat top-level
          // fields and derive facilityCode — create responses omit encounter_location.
          const patient = apiDetailToPatient(created, useReferenceStore().facilities);
          this.patients.unshift(patient);
          this.total++;
          this.allPatientsTotal++;
          this._enrichPatients();

          auditStatus = 'SUCCESS';
          auditResourceId = patient.id;
          auditNewValue = body;
          return patient;
        }

        // Mock fallback
        const api = getApiClient();
        const { data: patient } = await api.post<Patient>('/api/patients', body);
        this.patients.unshift(patient);
        this.total++;
        this.allPatientsTotal++;
        auditStatus = 'SUCCESS';
        auditResourceId = patient.id;
        auditNewValue = body;
        return patient;
      } catch (err) {
        this.error = isDuplicateMrnError(err)
          ? i18next.t('apiErrors.duplicateMrn')
          : getApiErrorMessage(err, 'patient.failedCreate');
        auditStatus = 'FAILURE';
        auditErrorCode = 'CREATE_PATIENT_FAILED';
        auditErrorMessage = this.error ?? 'Failed to create patient';
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'CREATE_PATIENT_FAILED';
        }
        return null;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'PATIENT',
            eventType: 'REGISTER',
            eventStatus: auditStatus,
            resourceType: 'patients',
            resourceId: auditResourceId,
            isPhiAccess: true,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async updatePatient(id: string, patientData: Partial<Patient>): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditOldValue: unknown | undefined;
      let auditNewValue: unknown | undefined;

      // Snapshot existing patient for diff computation.
      // If not in this.patients (e.g. direct navigation), fetch it first.
      let existingPatient = this.patients.find((p) => p.id === id);
      if (!existingPatient && !this.useMockData) {
        try {
          const { data: detailResp } = await apiGetPatientById(id);
          const detail = extractDetail<ApiPatientDetail>(detailResp);
          existingPatient = apiDetailToPatient(detail, useReferenceStore().facilities);
        } catch {
          // Non-critical — audit will fall back to no oldValue
        }
      }

      try {
        if (!this.useMockData) {
          const body: UpdatePatientRequest = buildUpdatePatientBody(patientData);

          const apiResponse = await apiUpdatePatient(id, body);
          const response = apiResponse.data;
          // Normalize: plain object or wrapped envelope
          const updated = extractDetail<ApiPatientDetail>(response);
          // Pass facilities so apiDetailToPatient can fall back to flat top-level
          // fields and derive facilityCode — update responses omit encounter_location.
          const patient = apiDetailToPatient(updated, useReferenceStore().facilities);

          const index = this.patients.findIndex((p) => p.id === id);
          if (index !== -1) {
            this.patients[index] = patient;
          }
          this._enrichPatients();

          auditStatus = 'SUCCESS';
          // Compute diff: only include fields that actually changed
          if (existingPatient) {
            const diff = computeAuditDiff(existingPatient, body as unknown as Record<string, unknown>);
            auditOldValue = diff.oldValue;
            auditNewValue = diff.newValue;
          } else {
            auditNewValue = body;
          }
          return true;
        }

        // Mock fallback
        const api = getApiClient();
        const { data: patient } = await api.patch<Patient>(`/api/patients/${id}`, patientData);
        const index = this.patients.findIndex((p) => p.id === id);
        if (index !== -1) {
          this.patients[index] = patient;
        }
        auditStatus = 'SUCCESS';
        if (existingPatient) {
          const diff = computeAuditDiff(existingPatient, patientData as unknown as Record<string, unknown>);
          auditOldValue = diff.oldValue;
          auditNewValue = diff.newValue;
        } else {
          auditNewValue = patientData;
        }
        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'apiErrors.failedUpdatePatient');
        auditStatus = 'FAILURE';
        auditErrorCode = 'UPDATE_PATIENT_FAILED';
        auditErrorMessage = this.error ?? 'Failed to update patient';
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'UPDATE_PATIENT_FAILED';
        }
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'PATIENT',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'patients',
            resourceId: id,
            isPhiAccess: true,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async deletePatient(id: string): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditOldValue: unknown | undefined;

      // Snapshot existing patient before deletion for audit oldValue
      const existingPatient = this.patients.find((p) => p.id === id);
      if (existingPatient) {
        auditOldValue = patientToApiSnapshot(existingPatient);
      }

      try {
        await apiDeletePatient(id);

        // Remove from local list and keep counts in sync
        this.patients = this.patients.filter((p) => p.id !== id);
        this.total--;
        this.allPatientsTotal = Math.max(0, this.allPatientsTotal - 1);

        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'apiErrors.failedDeletePatient');
        auditStatus = 'FAILURE';
        auditErrorCode = 'DELETE_PATIENT_FAILED';
        auditErrorMessage = this.error ?? 'Failed to delete patient';
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'DELETE_PATIENT_FAILED';
        }
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'PATIENT',
            eventType: 'DELETE',
            eventStatus: auditStatus,
            resourceType: 'patients',
            resourceId: id,
            isPhiAccess: true,
            oldValue: auditOldValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    clearError() {
      this.error = null;
    },

    togglePatientSelection(patientId: string) {
      if (this.selectedPatientIds.has(patientId)) {
        this.selectedPatientIds.delete(patientId);
      } else {
        this.selectedPatientIds.add(patientId);
      }
    },

    selectAllPatients() {
      this.patients.forEach(p => this.selectedPatientIds.add(p.id));
    },

    deselectAllPatients() {
      this.selectedPatientIds.clear();
    },

    toggleAllPatients() {
      if (this.selectedPatientIds.size === this.patients.length) {
        this.deselectAllPatients();
      } else {
        this.selectAllPatients();
      }
    },

    /**
     * Recalculate patients list, pagination, selection, and tab counts
     * after a cache mutation (assign / unassign). Keeps all derived
     * state in sync from a single place.
     */
    _refreshAfterCacheMutation() {
      const filtered = this._getFilteredPatients();
      const startIndex = 0;
      const endIndex = this.pageSize;

      this.patients = filtered.slice(startIndex, endIndex);
      this.total = filtered.length;
      this.currentPage = 1;
      this.hasMore = endIndex < filtered.length;
      this._enrichPatients();
      this.deselectAllPatients();

      this.allPatientsTotal = this._getViewPatients('all').length;
      this.myPatientsTotal = this._getViewPatients('my').length;
    },

    /**
     * Assign patients to the current user.
     * In real mode calls POST /patientAssignment,
     * in mock mode mutates the local cache.
     */
    async assignPatients(patientIds: string[]): Promise<boolean> {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      const staffId = useAuthStore().user?.id || config.staffId;

      if (!this.useMockData) {
        try {
          if (!staffId) {
            this.error = 'Cannot assign: no authenticated user or VITE_STAFF_ID configured';
            return false;
          }

          // Build the assignment payload from current patients
          const patients = patientIds
            .map((id) => {
              const p = this.patients.find((pt) => pt.id === id);
              if (!p) return null;
              const encId = p.encounterId;
              if (!encId) {
                this.error = `Cannot assign patient ${id}: no active encounter found`;
                return null;
              }
              return {
                patientId: id,
                encounterId: encId,
                hospitalId: p.hospitalId || '',
              };
            })
            .filter(Boolean) as {
            patientId: string;
            encounterId: string;
            hospitalId: string;
          }[];

          if (patients.length === 0) {
            return false; // error was already set in the map above
          }

          await assignPatientsToStaff(staffId, patients);
          // Update local tracking & refresh the list for the current view
          patientIds.forEach((id) => this.myPatientIds.add(id));
          this.myWorkloadCount = this.myPatientIds.size;
          await this.fetchPatients({ viewFilter: this.activeView });
          await this.loadMyWorkloadCount();
          auditStatus = 'SUCCESS';
          return true;
        } catch (err) {
          this.error = getApiErrorMessage(err, 'patient.failedAssignPlural');
          auditStatus = 'FAILURE';
          auditErrorCode = 'ASSIGN_PATIENT_FAILED';
          auditErrorMessage = this.error ?? 'Failed to assign patients';
          if (isAxiosError(err)) {
            const apiError = err.response?.data?.error;
            auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'ASSIGN_PATIENT_FAILED';
          }
          return false;
        } finally {
          try {
            logAuditEvent({
              eventCategory: 'PATIENT',
              eventType: 'ASSIGN',
              eventStatus: auditStatus,
              resourceType: 'patientAssignment',
              resourceId: staffId,
              isPhiAccess: true,
              newValue: { staffId, patientIds },
              errorCode: auditErrorCode,
              errorMessage: auditErrorMessage,
            });
          } catch {
            // Audit must never block the flow
          }
        }
      }

      // Mock fallback
      const cache = this.generateDummyPatients();
      const userEmail = this._getUserEmail();

      const idSet = new Set(patientIds);
      for (const p of cache) {
        if (idSet.has(p.id)) {
          p.email = userEmail;
        }
      }
      patientIds.forEach((id) => this.myPatientIds.add(id));

      this._refreshAfterCacheMutation();
      auditStatus = 'SUCCESS';
      try {
        logAuditEvent({
          eventCategory: 'PATIENT',
          eventType: 'ASSIGN',
          eventStatus: auditStatus,
          resourceType: 'patientAssignment',
          resourceId: staffId,
          isPhiAccess: true,
          newValue: { staffId, patientIds },
          errorCode: auditErrorCode,
          errorMessage: auditErrorMessage,
        });
      } catch {
        // Audit must never block the flow
      }
      return true;
    },

    /**
     * Unassign patients from the current user.
     * In real mode calls PATCH /patientAssignment/:staff_id,
     * in mock mode clears their email in the local cache.
     */
    async unassignPatients(patientIds: string[]): Promise<boolean> {
      const staffId = useAuthStore().user?.id || config.staffId;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      if (!this.useMockData) {
        try {
          if (!staffId) {
            this.error = 'Cannot unassign: no authenticated user or VITE_STAFF_ID configured';
            return false;
          }

          await unassignPatientsFromStaff(staffId, patientIds);
          // Update local tracking & refresh the list for the current view
          patientIds.forEach((id) => this.myPatientIds.delete(id));
          this.myWorkloadCount = this.myPatientIds.size;
          await this.fetchPatients({ viewFilter: this.activeView });
          await this.loadMyWorkloadCount();
          auditStatus = 'SUCCESS';
          return true;
        } catch (err) {
          this.error = getApiErrorMessage(err, 'patient.failedUnassignPlural');
          auditStatus = 'FAILURE';
          auditErrorCode = 'UNASSIGN_PATIENT_FAILED';
          auditErrorMessage = this.error ?? 'Failed to unassign patients';
          if (isAxiosError(err)) {
            const apiError = err.response?.data?.error;
            auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'UNASSIGN_PATIENT_FAILED';
          }
          return false;
        } finally {
          try {
            logAuditEvent({
              eventCategory: 'PATIENT',
              eventType: 'UNASSIGN',
              eventStatus: auditStatus,
              resourceType: 'patientAssignment',
              resourceId: staffId,
              isPhiAccess: true,
              oldValue: { staffId, patientIds },
              errorCode: auditErrorCode,
              errorMessage: auditErrorMessage,
            });
          } catch {
            // Audit must never block the flow
          }
        }
      }

      // Mock fallback
      const cache = this.generateDummyPatients();

      const idSet = new Set(patientIds);
      for (const p of cache) {
        if (idSet.has(p.id)) {
          p.email = '';
        }
      }
      patientIds.forEach((id) => this.myPatientIds.delete(id));

      this._refreshAfterCacheMutation();
      auditStatus = 'SUCCESS';
      try {
        logAuditEvent({
          eventCategory: 'PATIENT',
          eventType: 'UNASSIGN',
          eventStatus: auditStatus,
          resourceType: 'patientAssignment',
          resourceId: staffId,
          isPhiAccess: true,
          oldValue: { staffId, patientIds },
          errorCode: auditErrorCode,
          errorMessage: auditErrorMessage,
        });
      } catch {
        // Audit must never block the flow
      }
      return true;
    },

    /**
     * Clinically discharge a patient (POST /patients/:id/discharge).
     * Closes the active encounter, deactivates encounter-scoped records, and
     * frees the bed when one is assigned. Returns the refreshed patient detail
     * on success (is_active = false) or null on failure.
     */
    async dischargePatient(
      id: string,
      body: DischargePatientRequest = {},
      auditMotherInfo?: { motherMrn?: string; motherName?: string },
    ): Promise<Patient | null> {
      this.isLoading = true;
      this.error = null;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      // Captured up-front so the audit trail can identify the patient even on
      // failure, and so it doesn't depend on the post-discharge refresh below.
      const cachedPatient = this.patients.find((p) => p.id === id);
      const auditPatientMrn = cachedPatient?.medicalRecordNumber ?? null;
      const auditPatientName = cachedPatient?.name ?? null;

      try {
        if (!this.useMockData) {
          const apiResponse = await apiDischargePatient(id, body);
          const dischargeResult = extractDetail<ApiDischargeResult>(apiResponse.data);
          this.lastMotherAutoDischarge = dischargeResult.mother_auto_discharge
            ? {
              triggered: dischargeResult.mother_auto_discharge.triggered,
              personsDischarged: dischargeResult.mother_auto_discharge.persons_discharged,
            }
            : null;
          this.lastPreparedFeedsSentHomeCount = dischargeResult.prepared_feeds_sent_home?.length ?? null;
          // Discharge closes all patient_assignments server-side -- keep the
          // local myPatientIds cache in sync so isAssignedToMe reflects the
          // unassignment immediately, without waiting for a full reload.
          this.myPatientIds.delete(id);
          this.myWorkloadCount = this.myPatientIds.size;
          // Refresh from the server so the full detail (is_active, encounter
          // status, location, etc.) is authoritative rather than hand-rolled.
          const refreshed = await this.getPatient(id);
          // If the now-discharged patient no longer matches the active ADT
          // status filter (e.g. viewing "Active" only), drop it from the
          // visible list immediately instead of leaving a stale row.
          if (refreshed && !matchesAdtStatusFilter(refreshed, this.activeAdtStatusFilters)) {
            this.patients = this.patients.filter((p) => p.id !== id);
            if (this.total > 0) this.total -= 1;
          }
          auditStatus = 'SUCCESS';
          return refreshed;
        }

        // Mock fallback -- flip isActive and clear assignment locally
        const idx = this.patients.findIndex((p) => p.id === id);
        if (idx !== -1) {
          const updated = { ...this.patients[idx], isActive: false, isAssignedToMe: false };
          this.myPatientIds.delete(id);
          this.myWorkloadCount = this.myPatientIds.size;
          if (!matchesAdtStatusFilter(updated, this.activeAdtStatusFilters)) {
            this.patients = this.patients.filter((p) => p.id !== id);
            if (this.total > 0) this.total -= 1;
          } else {
            this.patients[idx] = updated;
          }
          auditStatus = 'SUCCESS';
          return updated;
        }
        return null;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'apiErrors.failedDischargePatient');
        auditStatus = 'FAILURE';
        auditErrorCode = 'DISCHARGE_PATIENT_FAILED';
        auditErrorMessage = this.error ?? 'Failed to discharge patient';
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'DISCHARGE_PATIENT_FAILED';
        }
        return null;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'PATIENT',
            eventType: 'DISCHARGE',
            eventStatus: auditStatus,
            resourceType: 'patients',
            resourceId: id,
            isPhiAccess: true,
            newValue: {
              record_type: 'PATIENT',
              patient_mrn: auditPatientMrn,
              patient_name: auditPatientName,
              ...body,
              ...(body.discharge_mother && {
                mother_mrn: auditMotherInfo?.motherMrn ?? null,
                mother_name: auditMotherInfo?.motherName ?? null,
              }),
            },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Re-admit a previously discharged patient (POST /patients/:id/readmit).
     * Creates a brand-new encounter (new episode of care) and restores
     * is_active = true. Returns the refreshed patient detail on success or
     * null on failure.
     */
    async readmitPatient(id: string, body: ReadmitPatientRequest = {}): Promise<Patient | null> {
      this.isLoading = true;
      this.error = null;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      // Captured up-front so the audit trail can identify the patient even on
      // failure, and so it doesn't depend on the post-readmit refresh below.
      const cachedPatient = this.patients.find((p) => p.id === id);
      const auditPatientMrn = cachedPatient?.medicalRecordNumber ?? null;
      const auditPatientName = cachedPatient?.name ?? null;

      try {
        if (!this.useMockData) {
          const apiResponse = await apiReadmitPatient(id, body);
          const readmitResult = extractDetail<ApiReadmitResult>(apiResponse.data);
          this.lastMotherAutoReadmit = readmitResult.mother_auto_readmit
            ? {
              triggered: readmitResult.mother_auto_readmit.triggered,
              personId: readmitResult.mother_auto_readmit.person_id,
            }
            : null;
          const refreshed = await this.getPatient(id);
          // If the now-readmitted patient no longer matches the active ADT
          // status filter (e.g. viewing "Discharged" only), drop it from the
          // visible list immediately instead of leaving a stale row.
          if (refreshed && !matchesAdtStatusFilter(refreshed, this.activeAdtStatusFilters)) {
            this.patients = this.patients.filter((p) => p.id !== id);
            if (this.total > 0) this.total -= 1;
          }
          auditStatus = 'SUCCESS';
          return refreshed;
        }

        // Mock fallback -- flip isActive locally
        const idx = this.patients.findIndex((p) => p.id === id);
        if (idx !== -1) {
          const updated = { ...this.patients[idx], isActive: true };
          if (!matchesAdtStatusFilter(updated, this.activeAdtStatusFilters)) {
            this.patients = this.patients.filter((p) => p.id !== id);
            if (this.total > 0) this.total -= 1;
          } else {
            this.patients[idx] = updated;
          }
          auditStatus = 'SUCCESS';
          return updated;
        }
        return null;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'apiErrors.failedReadmitPatient');
        auditStatus = 'FAILURE';
        auditErrorCode = 'READMIT_PATIENT_FAILED';
        auditErrorMessage = this.error ?? 'Failed to readmit patient';
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'READMIT_PATIENT_FAILED';
        }
        return null;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'PATIENT',
            eventType: 'READMIT',
            eventStatus: auditStatus,
            resourceType: 'patients',
            resourceId: id,
            isPhiAccess: true,
            newValue: {
              record_type: 'PATIENT',
              patient_mrn: auditPatientMrn,
              patient_name: auditPatientName,
              ...body,
            },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Load the total count of all patients (without affecting this.patients).
     * Called on mount so the "All Patients" tab always shows the correct count
     * even when the initial fetch is scoped to "My Patients".
     */
    async loadAllPatientsCount() {
      if (this.useMockData) {
        this.allPatientsTotal = this._getViewPatients('all').length;
        return;
      }
      // Skip when activeView is 'all' -- fetchPatients already set allPatientsTotal.
      if (this.activeView === 'all') return;
      try {
        const countRes = await apiFetchPatients({ page: 1, limit: 100 });
        const pagination =
          (countRes as WithPagination<typeof countRes>).pagination ??
          extractPagination(countRes.data);
        const total = pagination?.totalItems ?? extractListItems<ApiPatientListItem>(countRes.data).length;
        this.allPatientsTotal = total;
      } catch {
        // Non-critical -- keep whatever value was already set
      }
    },

    /**
     * Load the authoritative workload count for the current staff member
     * via GET /api/v1/patient-staff/staff/:staffId/patient-count.
     * In mock mode, falls back to the myPatientIds size.
     */
    async loadMyWorkloadCount() {
      if (!this.useMockData) {
        // try {
        //   const staffId = config.staffId;
        //   if (!staffId) return;
        //   const { data } = await getPatientCountForStaff(staffId);
        //   this.myWorkloadCount = data?.patientCount ?? 0;
        // } catch {
        //   // Silently fall back to local count
        //   this.myWorkloadCount = this.myPatientIds.size;
        // }
        this.myWorkloadCount = this.myPatientIds.size;
      } else {
        this.myWorkloadCount = this.myPatientIds.size;
      }
    },

    /**
     * Reset the cached patient data. Call on logout or when
     * the underlying data needs to be regenerated.
     */
    resetCache() {
      this._allPatientsCache = null;
      this._cacheUserEmail = '';
    },
  },

  persist: false, // Don't persist patients data
});
